import { useEffect, useState } from "react";
import { api, fmtBRL, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Modal, Btn } from "./ui";

const months = (d) => `${d} ${d === 1 ? "mês" : "meses"}`;
const telas = (n) => `${n} tela${n > 1 ? "s" : ""}`;

const PkgGroup = ({ n, pkgs, c, sel, setSel }) => (
  <div data-testid={`renew-group-${n}`}>
    <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
      {telas(n)}{n === (c.connections || 1) && !c.is_trial && <span className="ml-2 normal-case tracking-normal text-sky-300">telas atuais</span>}
    </div>
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {pkgs.map((x) => (
        <button key={x.id} data-testid={`renew-pkg-${x.connections}-${x.duration}`} onClick={() => setSel(x.id)}
          className={`rounded-xl border p-2.5 text-left transition-colors ${sel === x.id ? "border-sky-500 bg-sky-500/10" : "border-[#1F293D] bg-[#111827] hover:border-[#334155]"}`}>
          <div className="whitespace-nowrap font-mono text-sm font-bold text-white">{months(x.duration)}</div>
          <div className="whitespace-nowrap text-xs text-slate-300">{fmtBRL(x.plan_price / 100)}</div>
          <div className="whitespace-nowrap text-[11px] text-slate-500">{x.est_credits} créd.</div>
          {x.name === c.plan && <div className="mt-1 inline-block rounded bg-sky-500/20 px-1 text-[10px] font-semibold text-sky-300">ATUAL</div>}
        </button>
      ))}
    </div>
  </div>
);

export const RenewDialog = ({ c, onClose, onDone }) => {
  const [pkgs, setPkgs] = useState(null);
  const [sel, setSel] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/clients/${c.id}/packages`).then((r) => {
      setPkgs(r.data);
      setSel((r.data.find((p) => p.name === c.plan) || r.data.find((p) => !p.change_screens) || r.data[0] || {}).id || "");
    }).catch((e) => { toast.error(e.response?.data?.detail || "Erro ao carregar pacotes"); onClose(); });
  }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const p = (pkgs || []).find((x) => x.id === sel);

  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${c.id}/renew`, { package_id: sel });
      toast.success(`${c.is_trial ? "Ativado" : "Renovado"} até ${fmtDate(data.client?.due_date)}${data.credits_used ? ` · ${data.credits_used} créditos usados` : ""} · saldo ${data.credits ?? "?"}`);
      onDone(data.client);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao renovar"); }
    setBusy(false);
  };

  return (
    <Modal testid="renew-dialog" title={`${c.is_trial ? "Ativar" : "Renovar"} ${c.name || c.panel_user}`} onClose={onClose}>
      {!pkgs ? <p className="text-sm text-slate-400">Carregando pacotes...</p> : (
        <>
          <div className="max-h-80 space-y-3 overflow-y-auto pr-1">
            {[...new Set(pkgs.map((x) => x.connections))].map((n) => (
              <PkgGroup key={n} n={n} pkgs={pkgs.filter((x) => x.connections === n)} c={c} sel={sel} setSel={setSel} />
            ))}
          </div>
          {p && (
            <div data-testid="renew-confirm-text" className="mt-4 flex gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {c.is_trial ? "Ativar como cliente pagante" : "Renovar"} <b>{c.name || c.panel_user}</b> por <b>{months(p.duration)}</b> com <b>{telas(p.connections)}</b> ({fmtBRL(p.plan_price / 100)})? Vai consumir cerca de <b>{p.est_credits} crédito{p.est_credits > 1 ? "s" : ""}</b> da conta <b>{c.account}</b>. Vencimento atual: {fmtDate(c.due_date)}.
                {p.change_screens && <span data-testid="renew-change-screens" className="mt-1 block font-semibold text-amber-100">As telas mudam de {c.connections || 1} para {p.connections} no SYSLOG antes de renovar. Ao aumentar, o SYSLOG cobra também a diferença proporcional até o vencimento atual.</span>}
              </span>
            </div>
          )}
          <div className="mt-5 flex justify-end gap-2">
            <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
            <Btn data-testid="renew-confirm" tone="emerald" disabled={!sel || busy} onClick={go}>{busy ? "Processando..." : c.is_trial ? "Confirmar ativação" : "Confirmar renovação"}</Btn>
          </div>
        </>
      )}
    </Modal>
  );
};
